/**
 * Upload a file to S3 using a presigned URL.
 * Uses XMLHttpRequest instead of fetch to support upload progress events.
 *
 * @param url          - Presigned S3 PUT URL.
 * @param file         - File to upload.
 * @param onProgress   - Optional callback receiving bytes loaded so far.
 * @param extraHeaders - Optional additional request headers (e.g. `x-amz-tagging`
 *                       when the presigned URL was generated with object tagging).
 */
export const uploadFileToS3 = (
  url: string,
  file: File,
  onProgress?: (bytesLoaded: number) => void,
  extraHeaders?: Record<string, string>
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error('Upload failed. Please try again.'));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed. Please try again.'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload failed. Please try again.'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    if (extraHeaders) {
      for (const [key, value] of Object.entries(extraHeaders)) {
        xhr.setRequestHeader(key, value);
      }
    }
    xhr.send(file);
  });
};
